import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  InjectionToken,
  input,
  model,
} from '@angular/core';
import { clsx } from 'clsx';

let nextId = 0;

// Injection token for the accordion panel component
export const ACCORDION_PANEL = new InjectionToken<AccordionPanelComponent>('ACCORDION_PANEL');

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'sb-accordion-panel',
  templateUrl: './accordion-panel.component.html',
  host: {
    '[id]': 'id()',
    '[class]': 'hostClasses()',
  },
  exportAs: 'accordionPanel',
  providers: [{ provide: ACCORDION_PANEL, useExisting: AccordionPanelComponent }],
})
export class AccordionPanelComponent {
  private elementRef = inject(ElementRef<HTMLElement>);

  /**
   * Unique identifier for the accordion panel
   */
  id = input<string>(`sb-accordion-panel-${nextId++}`);

  /**
   * Whether the accordion panel is disabled
   */
  disabled = input<boolean>(false);

  /**
   * Whether the accordion panel is toggleable by clicking on the header
   */
  toggleable = input<boolean>(true);

  variant = input<'default' | 'flat'>('default');

  /**
   * Drops the content padding while keeping the panel's border, so content such as a table can run
   * edge to edge inside the card.
   */
  noPadding = input(false);

  /**
   * Signal for expanded state with two-way binding
   */
  expanded = model<boolean>(false);

  hostClasses = computed(() =>
    clsx(
      'block w-full bg-surface-page',
      this.variant() === 'flat' ? 'rounded-none border-0' : 'rounded-xl border border-solid border-default',
      // Edge-to-edge content (noPadding) stays clipped while open too, or its square corners would
      // paint over the panel's rounded border. clip, unlike hidden, keeps sticky content working.
      (!this.expanded() || this.noPadding()) && 'overflow-clip',
    ),
  );

  regionClasses = computed(() =>
    clsx(
      'grid transition-accordion',
      this.expanded()
        ? 'grid-rows-collapse-expanded opacity-100 duration-300'
        : 'grid-rows-collapse opacity-40 duration-250',
    ),
  );

  // Content stays clipped until the row has finished growing, then may overflow
  // (focus rings, shadows). Collapsing clips immediately.
  // clip, not hidden: hidden makes the element a scroll container, so a sticky header inside the
  // panel would stick to it instead of the page's scroll viewport and drop into the rows while the
  // panel animates.
  // min-w-0: once overflow is visible, the grid item's automatic minimum width becomes its
  // content's min-content width, so wide content (a table) would stretch the track past the panel.
  clipClasses = computed(() =>
    clsx(
      'min-h-0 min-w-0',
      this.expanded()
        ? 'overflow-visible transition-accordion-overflow transition-discrete delay-300 duration-0'
        : 'overflow-clip',
    ),
  );

  contentClasses = computed(() => clsx(!this.noPadding() && 'px-md pb-md'));

  /**
   * Computed signal for header ID
   */
  headerId = computed(() => `${this.id()}-header`);

  /**
   * Computed signal for content ID
   */
  contentId = computed(() => `${this.id()}-content`);

  /**
   * Toggle the expanded state
   */
  toggle(): void {
    if (this.disabled()) return;

    // Update the state
    const newValue = !this.expanded();
    this.expanded.set(newValue);
  }

  /**
   * Set expanded state
   */
  setExpanded(isExpanded: boolean): void {
    if (this.expanded() === isExpanded) return;

    this.expanded.set(isExpanded);
  }

  /**
   * Focus the header element of this panel
   */
  focus(): void {
    const headerElement = this.elementRef.nativeElement.querySelector('[role="button"]');
    if (headerElement instanceof HTMLElement) {
      headerElement.focus();
    }
  }
}
